import { SheetTextInput } from "@/components/ui/sheet-text-input";
import { BrandColors, Colors } from "@/constants/theme";
import { useDesktopSheetStyle } from "@/hooks/use-desktop-layout";
import { BottomSheetModal, BottomSheetScrollView } from "@gorhom/bottom-sheet";
import Ionicons from "@react-native-vector-icons/ionicons";
import React, { forwardRef, useMemo, useState } from "react";
import {
  StyleSheet,
  TouchableOpacity,
  View,
  useColorScheme,
} from "react-native";
import { ThemedText } from "./themed-text";

export interface BottomSheetOption {
  value: string;
  label: string;
  icon?: string;
}

export interface BottomSheetSection {
  title?: string;
  options: BottomSheetOption[];
}

interface BrandBottomSheetModalProps {
  title: string;
  subtitle?: string;
  sections?: BottomSheetSection[];
  options?: BottomSheetOption[];
  selectedValue?: string;
  onSelect: (value: string) => void;
  onClose?: () => void;
  snapPoints?: string[];
  children?: React.ReactNode;
  searchable?: boolean;
  searchPlaceholder?: string;
  multiple?: boolean;
  selectedValues?: string[];
}

export const BrandBottomSheetModal = forwardRef<
  BottomSheetModal,
  BrandBottomSheetModalProps
>(
  (
    {
      title,
      subtitle,
      sections,
      options,
      selectedValue,
      onSelect,
      onClose,
      snapPoints: BrandSnapPoints,
      children,
      searchable,
      searchPlaceholder = "Search...",
      multiple = false,
      selectedValues = [],
    },
    ref,
  ) => {
    const colorScheme = useColorScheme();
    const theme = Colors[colorScheme ?? "light"];
    const snapPoints = useMemo(
      () => BrandSnapPoints || ["50%", "75%", "100%"],
      [BrandSnapPoints],
    );
    const [searchQuery, setSearchQuery] = useState("");
    const desktopSheetStyle = useDesktopSheetStyle();

    const handleSelect = (value: string) => {
      onSelect(value);
      if (!multiple && onClose) {
        onClose();
      }
    };

    const isSelected = (value: string) => {
      if (multiple) {
        return selectedValues.includes(value);
      }
      return selectedValue === value;
    };

    const renderOption = (option: BottomSheetOption) => (
      <TouchableOpacity
        key={option.value}
        testID={`bottom-sheet-option-${option.value}`}
        accessible
        accessibilityLabel={option.label}
        style={[
          styles.serviceOption,
          { backgroundColor: theme.background, borderColor: theme.border },
          isSelected(option.value) && styles.serviceOptionSelected,
        ]}
        onPress={() => handleSelect(option.value)}
      >
        <View style={styles.optionContent}>
          {option.icon && (
            <Ionicons
              name={option.icon as any}
              size={20}
              color={
                isSelected(option.value) ? BrandColors.primary : theme.text
              }
              style={styles.optionIcon}
            />
          )}
          <ThemedText
            style={[
              isSelected(option.value) && styles.serviceOptionTextSelected,
            ]}
          >
            {option.label}
          </ThemedText>
        </View>
        {isSelected(option.value) && (
          <Ionicons name="checkmark" size={20} color={BrandColors.primary} />
        )}
      </TouchableOpacity>
    );

    const renderSection = (section: BottomSheetSection, index: number) => {
      const filteredOptions = section.options.filter((option) =>
        option.label.toLowerCase().includes(searchQuery.toLowerCase()),
      );

      if (filteredOptions.length === 0 && searchQuery !== "") return null;

      return (
        <View key={index} style={styles.serviceSection}>
          {section.title && (
            <ThemedText style={styles.serviceSectionHeader}>
              {section.title}
            </ThemedText>
          )}
          {filteredOptions.map(renderOption)}
        </View>
      );
    };

    const filteredOptions = useMemo(() => {
      if (!options) return [];
      return options.filter((option) =>
        option.label.toLowerCase().includes(searchQuery.toLowerCase()),
      );
    }, [options, searchQuery]);

    return (
      <BottomSheetModal
        ref={ref}
        index={1}
        snapPoints={snapPoints}
        style={desktopSheetStyle}
        onDismiss={() => setSearchQuery("")}
        backgroundStyle={{ backgroundColor: theme.background }}
        handleIndicatorStyle={styles.routeSheetHandle}
      >
        <View style={styles.routeSheetContent}>
          <View style={styles.routeSheetHeader}>
            <View style={{ gap: 4 }}>
              <ThemedText type="subtitle">{title}</ThemedText>
              {subtitle && <ThemedText type="small">{subtitle}</ThemedText>}
            </View>
            <TouchableOpacity onPress={onClose} style={styles.routeCloseButton}>
              <Ionicons name="close" size={20} color="#94a3b8" />
            </TouchableOpacity>
          </View>

          {searchable && (
            <View
              style={[
                styles.searchContainer,
                {
                  backgroundColor: theme.background,
                  borderColor: theme.border,
                },
              ]}
            >
              <Ionicons
                name="search"
                size={18}
                color={theme.text}
                style={styles.searchIcon}
              />
              <SheetTextInput
                value={searchQuery}
                onChangeText={setSearchQuery}
                placeholder={searchPlaceholder}
                placeholderTextColor={BrandColors.mediumGray}
                style={[styles.searchInput, { color: theme.text }]}
              />
              {searchQuery !== "" && (
                <TouchableOpacity onPress={() => setSearchQuery("")}>
                  <Ionicons
                    name="close-circle"
                    size={18}
                    color={BrandColors.mediumGray}
                  />
                </TouchableOpacity>
              )}
            </View>
          )}
        </View>
        <BottomSheetScrollView
          style={styles.routeSheetContent}
          showsVerticalScrollIndicator={false}
        >
          {children ? (
            children
          ) : sections ? (
            sections.map(renderSection)
          ) : options ? (
            <View style={styles.serviceSection}>
              {filteredOptions.map(renderOption)}
            </View>
          ) : null}
          <View style={{ height: 60 }} />
        </BottomSheetScrollView>
      </BottomSheetModal>
    );
  },
);

BrandBottomSheetModal.displayName = "BrandBottomSheetModal";

const styles = StyleSheet.create({
  routeSheetBackground: {
    backgroundColor: "#0f172a",
  },
  routeSheetHandle: {
    backgroundColor: "#334155",
  },
  routeSheetContent: {
    paddingHorizontal: 20,
  },
  routeSheetHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: 20,
  },
  routeCloseButton: {
    padding: 4,
  },
  serviceSection: {
    marginBottom: 24,
  },
  serviceSectionHeader: {
    fontSize: 14,
    fontWeight: "600",
    color: "#94a3b8",
    marginBottom: 12,
    textTransform: "uppercase",
    letterSpacing: 0.5,
  },
  serviceOption: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingVertical: 14,
    paddingHorizontal: 16,
    borderRadius: 12,
    marginBottom: 8,
    borderWidth: 1,
    borderColor: "transparent",
  },
  serviceOptionSelected: {
    borderColor: BrandColors.primary,
    backgroundColor: "rgba(52, 211, 153, 0.1)",
  },
  optionContent: {
    flexDirection: "row",
    alignItems: "center",
  },
  optionIcon: {
    marginRight: 12,
  },
  serviceOptionTextSelected: {
    color: BrandColors.primary,
    fontWeight: "600",
  },
  searchContainer: {
    flexDirection: "row",
    alignItems: "center",
    borderRadius: 12,
    paddingHorizontal: 12,
    marginBottom: 16,
    height: 48,
    borderWidth: 1,
  },
  searchIcon: {
    marginRight: 8,
  },
  searchInput: {
    flex: 1,
    fontSize: 14,
    paddingVertical: 8,
  },
});
